/**
 * StatusPanel — Wyświetla aktualny stan konfiguracji KSeF (NIP, cert, klucz, hasło, storage).
 *
 * Wyodrębnione z: /app/frontend/src/pages/settings/KsefSettingsPage.jsx
 * (linie 510-684 — JSX sekcji "Bezpieczeństwo / Przechowywanie")
 *
 * Props:
 *   - adminConfig (object|null)   — wynik GET /api/ksef/admin/config
 *   - storageInfo (object|null)   — wynik GET /api/ksef/storage/info
 *   - savedNip (string)
 *   - environment (string)
 */
import { Lock, Shield, CheckCircle, AlertCircle, FileCheck, FolderLock } from "lucide-react";

export const StatusPanel = ({
  adminConfig = null,
  storageInfo = null,
  savedNip = "",
  environment = "PROD",
}) => {
  const hasCert = !!adminConfig?.has_cert;
  const hasKey = !!adminConfig?.key_filename;
  const hasPassword = !!adminConfig?.has_key_password;

  return (
    <div className="space-y-4" data-testid="status-panel">
      {/* Storage info */}
      {storageInfo && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <FolderLock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <p className="font-medium text-blue-900">Bezpieczne przechowywanie</p>
              <div className="mt-2 space-y-1 text-blue-700 text-xs">
                <p>Lokalizacja: <span className="font-mono">{storageInfo.location || "—"}</span></p>
                {storageInfo.encryption && <p>Szyfrowanie: <span className="font-medium">{storageInfo.encryption}</span></p>}
                {storageInfo.permissions && <p>Uprawnienia: <span className="font-mono">{storageInfo.permissions}</span></p>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status karty */}
      <div className="grid grid-cols-2 gap-3">
        {/* NIP */}
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Shield className={`w-4 h-4 ${savedNip ? "text-green-600" : "text-gray-400"}`} />
            <p className="text-xs font-medium text-gray-700">NIP podmiotu</p>
          </div>
          {savedNip ? (
            <p className="text-sm font-mono text-gray-900">
              {savedNip.slice(0, 3)}***{savedNip.slice(-4)}
            </p>
          ) : (
            <p className="text-xs text-gray-400">Brak</p>
          )}
        </div>

        {/* Środowisko */}
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Shield className="w-4 h-4 text-green-600" />
            <p className="text-xs font-medium text-gray-700">Środowisko</p>
          </div>
          <p className="text-sm font-medium text-gray-900">{environment}</p>
        </div>

        {/* Certyfikat */}
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <FileCheck className={`w-4 h-4 ${hasCert ? "text-green-600" : "text-gray-400"}`} />
            <p className="text-xs font-medium text-gray-700">Certyfikat</p>
          </div>
          {hasCert ? (
            <div>
              <p className="text-xs text-green-700 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Zapisany
              </p>
              {adminConfig?.cert_filename && (
                <p className="text-xs text-gray-500 mt-1 truncate">{adminConfig.cert_filename}</p>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-400 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Brak
            </p>
          )}
        </div>

        {/* Klucz prywatny */}
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Lock className={`w-4 h-4 ${hasKey ? "text-green-600" : "text-gray-400"}`} />
            <p className="text-xs font-medium text-gray-700">Klucz prywatny</p>
          </div>
          {hasKey ? (
            <div>
              <p className="text-xs text-green-700 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Zapisany
              </p>
              {adminConfig?.key_filename && (
                <p className="text-xs text-gray-500 mt-1 truncate">{adminConfig.key_filename}</p>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-400 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Brak (lub w .p12)
            </p>
          )}
        </div>
      </div>

      {/* Hasło do klucza */}
      <div className="bg-white border border-gray-200 rounded-lg p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lock className={`w-4 h-4 ${hasPassword ? "text-green-600" : "text-gray-400"}`} />
            <p className="text-sm font-medium text-gray-700">Hasło do klucza</p>
          </div>
          {hasPassword ? (
            <p className="text-xs text-green-700 flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              Zapisane (szyfrowane)
            </p>
          ) : (
            <p className="text-xs text-gray-400">Brak</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatusPanel;
